import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import axios from "axios";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, setMonth, setYear } from "date-fns";
import { tr } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ChevronLeft, ChevronRight, MapPin, Building2, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuditCalendar = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCalendarEvents();
  }, []);

  const fetchCalendarEvents = async () => {
    try {
      const response = await axios.get(`${API}/audits/calendar`, { withCredentials: true });
      setEvents(response.data);
    } catch (error) {
      console.error("Calendar error:", error);
    } finally {
      setLoading(false);
    }
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventDate = new Date(event.start);
      return isSameDay(eventDate, date);
    });
  };

  const selectedEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  const getStatusColor = (status) => {
    const colors = {
      planned: "bg-blue-500",
      in_progress: "bg-yellow-500",
      completed: "bg-green-500",
      cancelled: "bg-gray-400"
    };
    return colors[status] || colors.planned;
  };

  const weekDays = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

  // Month and year options
  const months = [
    { value: 0, label: "Ocak" },
    { value: 1, label: "Şubat" },
    { value: 2, label: "Mart" },
    { value: 3, label: "Nisan" },
    { value: 4, label: "Mayıs" },
    { value: 5, label: "Haziran" },
    { value: 6, label: "Temmuz" },
    { value: 7, label: "Ağustos" },
    { value: 8, label: "Eylül" },
    { value: 9, label: "Ekim" },
    { value: 10, label: "Kasım" },
    { value: 11, label: "Aralık" }
  ];

  // Generate year options (5 years back and 5 years forward)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

  const handleMonthChange = (monthValue) => {
    setCurrentDate(setMonth(currentDate, parseInt(monthValue)));
  };

  const handleYearChange = (yearValue) => {
    setCurrentDate(setYear(currentDate, parseInt(yearValue)));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  // Get empty cells for the start of the month
  const startDay = monthStart.getDay();
  const emptyStart = startDay === 0 ? 6 : startDay - 1;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="calendar-page">
      <div>
        <h1 className="text-2xl font-bold text-[#111827]">Denetim Takvimi</h1>
        <p className="text-[#4B5563]">Planlanmış denetimleri görüntüleyin</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2 border-[#E8F3EE]">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Button variant="ghost" size="icon" onClick={() => setCurrentDate(subMonths(currentDate, 1))}>
                <ChevronLeft className="w-5 h-5" />
              </Button>
              
              <div className="flex items-center gap-2">
                <Select value={currentDate.getMonth().toString()} onValueChange={handleMonthChange}>
                  <SelectTrigger className="w-[130px]" data-testid="month-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((month) => (
                      <SelectItem key={month.value} value={month.value.toString()}>
                        {month.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={currentDate.getFullYear().toString()} onValueChange={handleYearChange}>
                  <SelectTrigger className="w-[90px]" data-testid="year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={goToToday}
                  className="ml-2"
                  data-testid="go-today-btn"
                >
                  Bugün
                </Button>
              </div>
              
              <Button variant="ghost" size="icon" onClick={() => setCurrentDate(addMonths(currentDate, 1))}>
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-[#1A4D2E] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <>
                {/* Week Headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {weekDays.map((day) => (
                    <div key={day} className="text-center text-sm font-medium text-[#4B5563] py-2">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {/* Empty cells */}
                  {Array.from({ length: emptyStart }).map((_, i) => (
                    <div key={`empty-${i}`} className="aspect-square" />
                  ))}

                  {/* Days */}
                  {daysInMonth.map((day) => {
                    const dayEvents = getEventsForDate(day);
                    const isSelected = selectedDate && isSameDay(day, selectedDate);
                    const isToday = isSameDay(day, new Date());

                    return (
                      <button
                        key={day.toISOString()}
                        onClick={() => setSelectedDate(day)}
                        className={`aspect-square p-1 rounded-lg transition-all relative ${
                          isSelected
                            ? "bg-[#1A4D2E] text-white"
                            : isToday
                            ? "bg-[#E8F3EE] text-[#1A4D2E]"
                            : "hover:bg-[#F9FAFB]"
                        }`}
                        data-testid={`calendar-day-${format(day, "yyyy-MM-dd")}`}
                      >
                        <span className={`text-sm ${isSelected ? "font-bold" : ""}`}>
                          {format(day, "d")}
                        </span>
                        {dayEvents.length > 0 && (
                          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                            {dayEvents.slice(0, 3).map((event, i) => (
                              <div
                                key={i}
                                className={`w-1.5 h-1.5 rounded-full ${getStatusColor(event.status)}`}
                              />
                            ))}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Selected Day Events */}
        <Card className="border-[#E8F3EE]">
          <CardHeader>
            <CardTitle className="text-lg text-[#111827]">
              {selectedDate
                ? format(selectedDate, "d MMMM yyyy", { locale: tr })
                : "Bir gün seçin"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedDate ? (
              <p className="text-[#9CA3AF] text-center py-8">
                Detayları görmek için takvimden bir gün seçin
              </p>
            ) : selectedEvents.length === 0 ? (
              <p className="text-[#9CA3AF] text-center py-8">
                Bu tarihte denetim yok
              </p>
            ) : (
              <div className="space-y-4">
                {selectedEvents.map((event) => (
                  <div
                    key={event.id}
                    onClick={() => navigate(`/audits/${event.id}`)}
                    className="p-4 rounded-xl bg-[#F9FAFB] hover:bg-[#E8F3EE] cursor-pointer transition-all border-l-4 border-[#1A4D2E]"
                    data-testid={`event-${event.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-[#111827]">{event.title}</h3>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        event.status === "planned" ? "bg-blue-100 text-blue-800" :
                        event.status === "in_progress" ? "bg-yellow-100 text-yellow-800" :
                        event.status === "completed" ? "bg-green-100 text-green-800" :
                        "bg-gray-100 text-gray-800"
                      }`}>
                        {event.status === "planned" ? "Planlandı" :
                         event.status === "in_progress" ? "Devam Ediyor" :
                         event.status === "completed" ? "Tamamlandı" : "İptal"}
                      </span>
                    </div>
                    <div className="space-y-1 text-sm text-[#4B5563]">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-[#9CA3AF]" />
                        {event.customer_name}
                      </div>
                      {event.location && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-[#9CA3AF]" />
                          {event.location}
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-[#9CA3AF]" />
                        {format(new Date(event.start), "HH:mm", { locale: tr })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-6 p-4 bg-white rounded-lg border border-[#E8F3EE]">
        <span className="text-sm text-[#4B5563]">Durum:</span>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
          <span className="text-sm">Planlandı</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <span className="text-sm">Devam Ediyor</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className="text-sm">Tamamlandı</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-gray-400"></div>
          <span className="text-sm">İptal</span>
        </div>
      </div>
    </div>
  );
};

export default AuditCalendar;
